import {
    useRef, useLayoutEffect, useState, ReactNode,
} from 'react'

type Props = {
    children: ReactNode,
    minHeight: number,
}

/**
 * Wrapper component that maintains stable height.
 * Tracks the maximum height ever seen and never shrinks below it.
 * Prevents layout jumps when content changes size.
 */
export default function StableHeightWrapper({
    children,
    minHeight,
}: Readonly<Props>) {
    const containerRef = useRef<HTMLDivElement>(null)
    const [
        maxHeight,
        setMaxHeight,
    ] = useState<number>(minHeight)

    useLayoutEffect(() => {
        const element = containerRef.current

        if (!element) return undefined

        const updateHeight = () => {
            const newHeight = element.scrollHeight

            setMaxHeight((prev) => {
                return Math.max(prev, newHeight)
            })
        }

        updateHeight()

        const resizeObserver = new ResizeObserver(() => {
            updateHeight()
        })

        resizeObserver.observe(element)

        return () => {
            resizeObserver.disconnect()
        }
    }, [])

    return (
        <div
            ref={containerRef}
            style={{
                minHeight: maxHeight,
                overflow: 'visible',
            }}
        >
            {children}
        </div>
    )
}
